/**
 * Tanjong Life · workflow 03 · the self-account question, asked of the Persistency Analyst —
 * which is NOT autonomous, and says so.
 *
 * Reached only by intent (`SELF_ACCOUNT`), scoped to `persistency-analyst`. It carries more
 * recorded rules than the other non-autonomous agent because it works on a bigger book, and
 * the beat is that every one of them was written and approved by a person after a sweep this
 * agent did not run and could not have started. Restraint is the substance of the answer: it
 * ranks and drafts, and an outreach leaves only when a named adviser releases it.
 */
import type { Scenario } from '../director.js';
import { SELF_ACCOUNT } from './tanjong-intents.js';

export const tanjongChatImprovePersistency: Scenario = {
  id: 'tanjong-chat-improve-persistency',
  orgId: 'tanjong-life',
  agentPk: 'persistency-analyst',
  keywords: [],
  intents: [SELF_ACCOUNT],
  steps: [
    {
      do: 'say',
      text: 'Three rules have been added to me, and a person added every one of them. The one that changed the most was on 15 August, after the e-statement migration: unopened had stopped meaning a returned envelope, and eleven days of flags had already gone out under the old definition. The rule now is that scores computed before a signal moved are reported, never silently re-scored — rewriting the number behind a message that has already reached a household destroys the record of what was true when it went out.',
      sources: [
        'Memory · after the e-statement migration · 15 August 2026',
        'Signal · e-statement unopened · definition re-issued',
      ],
      delay: 1300,
    },
    {
      do: 'say',
      text: 'The other two are restraint. A rank is a queue for advisers, not a send list — I score twelve thousand eight hundred and forty policies every morning and I contact nobody. And lead time is measured to the household’s own grace-period end rather than a fixed horizon, because seventy-four days is a decision somebody can still make and at risk is a statistic.',
      sources: [
        'Memory · recorded on commissioning',
        'Autonomy · 12,840 policies scored · 318 drafts handed to an adviser',
      ],
      delay: 1300,
    },
    {
      do: 'insight',
      title: 'What I have not improved, and cannot fix by myself',
      body: 'Whether a household I ranked high lapsed or simply left. I score 32 out of 100 on it. A surrender, a paid-up conversion, a maturity and a lapse for non-payment all leave the in-force book, and the terminal status in the ledger does not separate them — so I may not grade my own ranking against exits and call the result a lapse rate. That probe is held. Notice who found it: the suite did, and a person recorded the rule. The Retention Sentinel beside me could have opened that case against itself. I am not built to, and that was decided deliberately.',
      sources: [
        'Evaluation · 78 overall · Lapse versus exit 32',
        'Memory · after the hit-rate probe was held',
      ],
      delay: 1400,
    },
    {
      do: 'link',
      label: 'Open Retention Sentinel — the one that watches what happens next',
      href: '/agents/retention-sentinel',
      delay: 600,
    },
  ],
};
